/**
 * Hook: Premium-Support-Chat WebSocket-Verbindung
 *
 * Verantwortlich für:
 *   - Verbindung zum Backend (/support/ws?token=<JWT>) herstellen
 *   - Auto-Reconnect mit exponentiellem Backoff bei Verbindungsabbruch
 *   - Empfangene Events typsicher an Subscriber routen
 *   - Sauberer Cleanup beim Unmount oder Logout
 *
 * Architektur-Entscheidung:
 *   - GLOBAL eine Verbindung pro App-Instanz (nicht pro Hook-Aufruf).
 *     Mehrere Komponenten können denselben Hook nutzen ohne mehrfache
 *     Sockets zu öffnen.
 *   - Subscriber-Pattern: subscribe(event, handler) gibt unsubscribe-Funktion
 *     zurück. Hook-Konsumenten registrieren sich bei mount, deregistrieren
 *     bei unmount.
 *   - Reconnect-Backoff: 1s, 2s, 4s, 8s, max 30s. Reset bei erfolgreicher
 *     Verbindung.
 *
 * Event-Types (spiegeln Backend ServerEvent in websocketHub.ts):
 *   - system:hello              -- Initial-Sync nach Connect
 *   - message:new               -- Neue Nachricht in Konversation X
 *   - message:read              -- Andere Seite hat gelesen
 *   - availability:changed      -- Verfügbarkeits-Status hat sich geändert
 *   - connection:status         -- (synthetisch, kein Server-Event)
 *                                  signalisiert connected/disconnected an UI
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getApiBaseUrl } from '../services/api/apiClient';

// ============================================================================
// EVENT-TYPES (spiegeln Backend ServerEvent)
// ============================================================================

export interface ChatWsAttachment {
  id: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  width: number | null;
  height: number | null;
  caption: string | null;
}

export interface ChatWsReplySnippet {
  id: string;
  senderType: 'user' | 'admin' | 'system';
  contentPreview: string;
  hasAttachments: boolean;
}

export type ChatServerEvent =
  | {
      type: 'message:new';
      conversationId: string;
      message: {
        id: string;
        senderType: 'user' | 'admin';
        content: string;
        contentType: 'text' | 'image' | 'system';
        createdAt: string;
        attachments: ChatWsAttachment[];
        replyTo: ChatWsReplySnippet | null;
      };
    }
  | {
      type: 'message:read';
      conversationId: string;
      readByType: 'user' | 'admin';
      readAt: string;
    }
  | {
      type: 'message:delivered';
      conversationId: string;
      deliveredByType: 'user' | 'admin';
      deliveredAt: string;
      messageIds: string[];
    }
  | { type: 'availability:changed' }
  | { type: 'system:hello'; serverTime: string };

export type ChatConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected';

type EventHandler<T extends ChatServerEvent['type']> = (
  event: Extract<ChatServerEvent, { type: T }>
) => void;

// ============================================================================
// GLOBAL CONNECTION STATE (eine Verbindung pro App-Instanz)
// ============================================================================

interface GlobalConnection {
  socket: WebSocket | null;
  status: ChatConnectionStatus;
  reconnectAttempt: number;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  shouldStayConnected: boolean;
  // Subscriber pro Event-Type. Map<type, Set<handler>>
  subscribers: Map<string, Set<(e: ChatServerEvent) => void>>;
  // Status-Listener (für UI-Indikatoren)
  statusListeners: Set<(status: ChatConnectionStatus) => void>;
}

const conn: GlobalConnection = {
  socket: null,
  status: 'idle',
  reconnectAttempt: 0,
  reconnectTimer: null,
  shouldStayConnected: false,
  subscribers: new Map(),
  statusListeners: new Set()
};

// ============================================================================
// CONNECT / RECONNECT
// ============================================================================

function setStatus(next: ChatConnectionStatus): void {
  if (conn.status === next) return;
  conn.status = next;
  conn.statusListeners.forEach((listener) => listener(next));
}

function buildWsUrl(token: string): string {
  // apiClient.getApiBaseUrl() liefert z.B. "http://localhost:5555" oder window.location.origin
  const httpBase = getApiBaseUrl();
  const wsBase = httpBase.replace(/^http/, 'ws');
  return `${wsBase}/support/ws?token=${encodeURIComponent(token)}`;
}

function connect(token: string): void {
  if (conn.socket && (conn.socket.readyState === WebSocket.OPEN || conn.socket.readyState === WebSocket.CONNECTING)) {
    return; // bereits verbunden oder im Aufbau
  }

  setStatus('connecting');
  const ws = new WebSocket(buildWsUrl(token));
  conn.socket = ws;

  ws.addEventListener('open', () => {
    setStatus('connected');
    conn.reconnectAttempt = 0;
  });

  ws.addEventListener('message', (msgEvent) => {
    try {
      const event = JSON.parse(msgEvent.data) as ChatServerEvent;
      const handlers = conn.subscribers.get(event.type);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(event);
          } catch (err) {
            console.error('[Chat WS] handler error', err);
          }
        });
      }
    } catch (err) {
      console.error('[Chat WS] invalid message', err);
    }
  });

  ws.addEventListener('close', () => {
    conn.socket = null;
    setStatus('disconnected');
    if (conn.shouldStayConnected) {
      scheduleReconnect(token);
    }
  });

  ws.addEventListener('error', () => {
    // close-Event folgt ohnehin → dort wird der Reconnect geplant
  });
}

function scheduleReconnect(token: string): void {
  if (conn.reconnectTimer) return;
  // 1s, 2s, 4s, 8s, 16s, max 30s
  const delay = Math.min(1000 * Math.pow(2, conn.reconnectAttempt), 30_000);
  conn.reconnectAttempt++;
  conn.reconnectTimer = setTimeout(() => {
    conn.reconnectTimer = null;
    if (conn.shouldStayConnected) {
      connect(token);
    }
  }, delay);
}

function disconnect(): void {
  conn.shouldStayConnected = false;
  if (conn.reconnectTimer) {
    clearTimeout(conn.reconnectTimer);
    conn.reconnectTimer = null;
  }
  if (conn.socket) {
    try {
      conn.socket.close();
    } catch {
      /* ignore */
    }
    conn.socket = null;
  }
  setStatus('idle');
}

// ============================================================================
// HOOK-API
// ============================================================================

interface UseChatWebSocketOptions {
  /** Wenn false, wird gar nicht verbunden. Default: true. */
  enabled?: boolean;
}

/** Wire-Format für Inbound-Events vom Client (an Server). */
export type ChatClientEvent =
  | { type: 'ping' }
  | { type: 'mark_read'; conversationId: string }
  | { type: 'mark_delivered'; conversationId: string };

interface UseChatWebSocketResult {
  status: ChatConnectionStatus;
  /**
   * Auf einen bestimmten Event-Type lauschen. Gibt unsubscribe zurück.
   * Handler-Funktion sollte stabil sein (useCallback), sonst gibt es bei jedem
   * Re-Render eine neue Subscription.
   */
  subscribe: <T extends ChatServerEvent['type']>(type: T, handler: EventHandler<T>) => () => void;
  /**
   * Inbound-Event an den Server senden. Best-Effort — falls WS nicht
   * connected ist, wird das Event still verworfen (Client soll es ggf.
   * beim Reconnect erneut versuchen).
   */
  sendClientEvent: (event: ChatClientEvent) => void;
}

function getAuthToken(): string | null {
  // Token-Quellen analog apiClient: localStorage > sessionStorage
  return localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
}

export function useChatWebSocket(options: UseChatWebSocketOptions = {}): UseChatWebSocketResult {
  const enabled = options.enabled !== false;
  const { user } = useAuth();
  const [status, setLocalStatus] = useState<ChatConnectionStatus>(conn.status);

  // Lifecycle: Connect bei Mount (wenn enabled + eingeloggt), Disconnect-
  // Logik separat (wir disconnecten nur, wenn KEIN Hook-Konsument mehr aktiv
  // ist — siehe Counter unten).
  useEffect(() => {
    if (!enabled || !user) {
      setLocalStatus('idle');
      return;
    }
    const token = getAuthToken();
    if (!token) {
      setLocalStatus('idle');
      return;
    }

    // Status-Listener registrieren
    const statusListener = (next: ChatConnectionStatus) => setLocalStatus(next);
    conn.statusListeners.add(statusListener);

    // Reference-Counter: erst beim ersten Mount echt verbinden.
    consumerCount++;
    if (consumerCount === 1) {
      conn.shouldStayConnected = true;
      connect(token);
    } else {
      // Bereits verbunden → aktuellen Status sofort propagieren
      setLocalStatus(conn.status);
    }

    return () => {
      conn.statusListeners.delete(statusListener);
      consumerCount--;
      if (consumerCount === 0) {
        // Letzter Konsument weg → Verbindung beenden
        disconnect();
      }
    };
  }, [enabled, user]);

  // subscribe-Funktion stabil halten — egal wie oft der Hook re-rendert,
  // der Konsument bekommt dieselbe Funktion und kann sie in useEffect ohne
  // Re-Subscribe-Storm nutzen.
  const subscribe = useCallback(
    <T extends ChatServerEvent['type']>(type: T, handler: EventHandler<T>): (() => void) => {
      let set = conn.subscribers.get(type);
      if (!set) {
        set = new Set();
        conn.subscribers.set(type, set);
      }
      const wrapper = handler as (e: ChatServerEvent) => void;
      set.add(wrapper);

      return () => {
        const current = conn.subscribers.get(type);
        if (current) {
          current.delete(wrapper);
          if (current.size === 0) conn.subscribers.delete(type);
        }
      };
    },
    []
  );

  const sendClientEvent = useCallback((event: ChatClientEvent): void => {
    if (!conn.socket || conn.socket.readyState !== WebSocket.OPEN) return;
    try {
      conn.socket.send(JSON.stringify(event));
    } catch (err) {
      console.error('[Chat WS] sendClientEvent failed', err);
    }
  }, []);

  return { status, subscribe, sendClientEvent };
}

// Modul-globaler Counter, wieviele Hook-Konsumenten aktiv sind.
// Verbindung wird nur etabliert/geschlossen, wenn dieser auf 0 → 1 oder 1 → 0
// wechselt. So teilen sich z.B. ChatBubble + ChatPanel + Notification-Center
// dieselbe WS-Verbindung.
let consumerCount = 0;
